import { CheckCircleOutlineOutlined } from "@mui/icons-material";
import { Box, styled } from "@mui/material";
import { createFileRoute, redirect, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";

import { BigotinSvg } from "~/assets/svg";
import { SignInForm } from "~/components/auth/sign-in-form";
import { useSignInState } from "~/hooks/use-sign-in-state";
import { type AppDispatch, setSigningIn } from "~/store";

type VerificationState = "verifying" | "success";

const Container = styled(Box)(({ theme }) => ({
  background: "linear-gradient(204deg, #4F6EF9 17.9%, #2848FB 87.14%)",
  minHeight: "100vh",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: theme.spacing(2),
}));

const BackgroundSvg = styled(BigotinSvg)({
  position: "absolute",
  left: "50%",
  top: "50%",
  transform: "translate(-50%, -50%)",
  width: "100%",
  maxWidth: 920,
  pointerEvents: "none",
});

export const Route = createFileRoute("/auth/verify")({
  component: VerifyEmailPage,
  beforeLoad: ({ context }) => {
    if (context.authState.isLoadingAuth) return;

    if (context.authState.isAuthenticated) {
      throw redirect({ to: "/" });
    }
  },
});

function VerifyEmailPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch<AppDispatch>();
  const { completeMagicLinkSignIn } = useSignInState();
  const [verificationState, setVerificationState] =
    useState<VerificationState>("verifying");

  const hasRunRef = useRef(false);

  const handleVerificationError = () => {
    navigate({
      to: "/login",
      search: {
        status: "expired",
      },
    });
  };

  const handleSuccessfulVerification = () => {
    setVerificationState("success");
    navigate({ to: "/" });
  };

  // biome-ignore lint/correctness/useExhaustiveDependencies: only run once on mount
  useEffect(() => {
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const verifyEmailLink = async () => {
      const emailLink = window.location.href;
      dispatch(setSigningIn(true));

      try {
        await completeMagicLinkSignIn(emailLink);
        // completeMagicLinkSignIn now handles both validation and user creation
        dispatch(setSigningIn(false));
        handleSuccessfulVerification();
      } catch {
        dispatch(setSigningIn(false));
        handleVerificationError();
      }
    };

    verifyEmailLink();
  }, []);

  const isVerifying = verificationState === "verifying";
  const isSuccess = verificationState === "success";

  return (
    <Container>
      <BackgroundSvg />
      <SignInForm
        mode="login"
        customTitle={isVerifying ? "Verificando correo..." : "Iniciando sesión"}
        customSubtitle={
          isVerifying
            ? "Esto puede tomar unos minutos"
            : "Te estamos redirigiendo ahora"
        }
        showLoading={true}
      >
        {isSuccess && (
          <CheckCircleOutlineOutlined color="success" sx={{ fontSize: 22 }} />
        )}
      </SignInForm>
    </Container>
  );
}
